import { put } from 'redux-saga/effects';

import { type GeneratorResult, selectTyped, takeEvery } from '@pressreader/appstore';
import { loadIssueInfoByIssueKey, loadIssueRestrictionsByIssueKey } from '@pressreader/content';
import { ArticleFields } from '@pressreader/content-types';
import { extractCid, extractDateString } from '@pressreader/content-utils';
import { navigateToAction, selectNavConfig } from '@pressreader/navigation-core';
import { ViewName } from '@pressreader/navigation-types';
import { isPageViewNavConfig, navigateToArticleInCurrentPageViewAction, PageViewNavConfig, TextViewNavConfig } from '@pressreader/views-navigation';

import { getNavigateToArticleActionByIssueInfo, resolveIssueLanguageAssertedByNavConfig } from '../../utils';
import { navigateToArticleByIssueKey, navigateToPageAction, navigateToTextAction } from '../actions';
import { loadArticleById } from '../saga.utils';

export function* navigateToPage({
    payload: { articleId, viewContextKey, zoomToArticle = true },
}: ReturnType<typeof navigateToPageAction>): GeneratorResult<void> {
    const article = yield* loadArticleById(articleId, { requiredFields: ArticleFields.Issue | ArticleFields.Publication });

    if (!article?.issue) {
        return;
    }

    const { issue } = article;
    const { issueKey } = issue;

    const restrictions = yield* loadIssueRestrictionsByIssueKey(issueKey);

    if (restrictions?.replica) {
        yield put(
            navigateToAction({
                viewName: ViewName.IssueDetails,
                params: {
                    cid: extractCid(issueKey),
                    issueDate: extractDateString(issueKey),
                    country: issue.slugs.country,
                    newspaper: issue.slugs.newspaper,
                },
                replace: !!viewContextKey,
            }),
        );
        return;
    }

    const currentNavConfig = yield* selectTyped(selectNavConfig);
    // The in-place reveal exists to zoom the PageView already on screen onto the article; a switch that
    // opted out of the zoom navigates to the page like any other entry instead.
    if (zoomToArticle && isPageViewNavConfig(currentNavConfig) && currentNavConfig.issueKey === issueKey) {
        yield put(navigateToArticleInCurrentPageViewAction({ issueKey, articleId }));
        return;
    }

    const params: Partial<PageViewNavConfig> = {
        contextKey: viewContextKey,
        issueKey: issueKey,
        cid: extractCid(issueKey),
        issueDate: extractDateString(issueKey),
        country: issue.slugs.country,
        newspaper: issue.slugs.newspaper,
        page: issue.page.number.toString(),
        // `articleId` IS the zoom intent: the PageView resolves it to the article's region and the swiper
        // zooms the page onto it. Omitted for a plain view switch, which lands on the page fitted instead.
        ...(zoomToArticle ? { articleId } : {}),
        // The PageView opens in the language the view on screen asserts for this issue, the original otherwise.
        language: resolveIssueLanguageAssertedByNavConfig({ navConfig: currentNavConfig, issueKey }),
    };

    yield put(navigateToAction({ viewName: ViewName.PagesView, params, replace: !!viewContextKey }));
}

export function* navigateToText({ payload }: ReturnType<typeof navigateToTextAction>): GeneratorResult<void> {
    const articleId = payload.articleId;
    const article = yield* loadArticleById(articleId, { requiredFields: ArticleFields.Issue | ArticleFields.Publication });

    if (!article?.issue) {
        return;
    }

    const { issue } = article;
    const { issueKey } = issue;

    const currentNavConfig = yield* selectTyped(selectNavConfig);
    const params: Partial<TextViewNavConfig> = {
        contextKey: payload.viewContextKey,
        issueKey: issueKey,
        cid: extractCid(issueKey),
        issueDate: extractDateString(issueKey),
        country: issue.slugs.country,
        newspaper: issue.slugs.newspaper,
        page: issue.page.number.toString(),
        // The TextView opens in the language the view on screen asserts for this issue, the original otherwise.
        language: resolveIssueLanguageAssertedByNavConfig({ navConfig: currentNavConfig, issueKey }),
    };

    yield put(navigateToAction({ viewName: ViewName.TextView, params, replace: !!payload.viewContextKey }));
}

function* handleNavigateToArticleByIssueKey({ payload: { articleId, issueKey, displayMode } }: ReturnType<typeof navigateToArticleByIssueKey>) {
    const issueInfo = yield* loadIssueInfoByIssueKey(issueKey);

    if (!issueInfo) {
        return;
    }

    const action = getNavigateToArticleActionByIssueInfo(issueInfo, articleId);

    yield put({
        ...action,
        payload: {
            ...action.payload,
            displayMode,
        },
    });
}

export function* saga() {
    yield takeEvery(navigateToPageAction, navigateToPage);
    yield takeEvery(navigateToTextAction, navigateToText);
    yield takeEvery(navigateToArticleByIssueKey, handleNavigateToArticleByIssueKey);
}
